// src/routes/AppRoutes.tsx
import React, { Suspense, use, useEffect, useState } from 'react';
import {  Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Header, Menus } from '../layouts';
import './route-styles';
import { NavLink } from 'react-router-dom';
import { DeviceDetailsContext } from '../store/contexts/UserDeviceContextProvider';
import { UserContext } from '../store/contexts/UserContextProvider';
import PageOverlay from '../components/pageoverlay/PageOverLay';
import { MetadataContext } from '../store/contexts/MetadataContextProvider';
import Logout from '../components/otplogin/logout';
import OtpLogin from '../components/otplogin/otplogin';
import AccessDenied from '../modules/TRM/pages/accessdenied/accessdenied';
import Chatbot from '../modules/TRM/pages/chatbot';
import { isProcessorDisabled } from '../Configuration';
import RouteErrorBoundary from './RouteErrorBoundary';
import { lazyWithRetry } from './lazyWithRetry';

// Route-level pages are code-split. Import page files directly (not the '../modules/TRM' barrel)
// so each page gets its own chunk instead of the barrel pulling every page into one bundle.
// Side-nav pages are preloaded after login (see AppRoutes); the rest are prefetched.
const TRMHome = lazyWithRetry(() => import('../modules/TRM/pages/home/home'));
const CustomerDashboard = lazyWithRetry(() => import('../modules/TRM/pages/home/customer-dashboard'));
const ProcessorDashboard = lazyWithRetry(() => import('../modules/TRM/pages/home/processor-dashboard'));
const TRMCreateInquiry = lazyWithRetry(() => import('../modules/TRM/pages/create-form/create-inquiry'));
const TRMInquiries = lazyWithRetry(() => import('../modules/TRM/pages/listview/inquiries'));
const TRMWorkOrders = lazyWithRetry(() => import('../modules/TRM/pages/listview/workorders'));
const TRMArchived = lazyWithRetry(() => import('../modules/TRM/pages/listview/archived'));
const TRMDetailview = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../modules/TRM/pages/detailview/detailview'));
const TRMKnowledgeGraph = lazyWithRetry(() => import('../modules/TRM/pages/knowledge-graph'));
const TRMQandA = lazyWithRetry(() => import('../modules/TRM/pages/qanda'));
const TRMAnnouncements = lazyWithRetry(() => import('../modules/TRM/pages/announcements'));
const TRMCalendar = lazyWithRetry(() => import('../modules/TRM/pages/calendar'));
const TRMSiteFeedback = lazyWithRetry(() => import('../modules/TRM/pages/site-feedback'));
const TRMSettings = lazyWithRetry(() => import('../modules/TRM/pages/settings/settings'));
const TRMMasterActivityLog = lazyWithRetry(() => import('../modules/TRM/pages/master-activity-log/master-activity-log'));
const TRMReleaseNotes = lazyWithRetry(() => import('../modules/TRM/pages/release-notes'));
const TRMProfile = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../modules/TRM/pages/profile/profile'));
const TRMImportData = lazyWithRetry(() => import('../modules/TRM/pages/import-data/import-data'));
const TRMRenewal = lazyWithRetry(() => import('../modules/TRM/pages/renewals/renewal'));
const TRMReportsData = lazyWithRetry(() => import('../modules/TRM/pages/reports-data/reports-data'));
const TRMGlobalSearch = lazyWithRetry(() => import(/* webpackPrefetch: true */ '../modules/TRM/pages/global-search/global-search'));
const Generate = lazyWithRetry(() => import('../shared/pages/generate/generate'));

const sideNavPages = [
  TRMCreateInquiry, TRMArchived, TRMReportsData, TRMImportData, TRMRenewal, TRMKnowledgeGraph, TRMQandA,
  TRMAnnouncements, TRMCalendar, TRMMasterActivityLog, TRMSettings, TRMSiteFeedback, TRMReleaseNotes,
];

// Navigation runs as a transition, which keeps the old page on screen while an already-visible
// boundary loads. A new (keyed) boundary shows its loader right away instead.
const withPageLoader = (key: string, element: React.ReactNode) => (
  <Suspense key={key} fallback={<PageOverlay/>}>{element}</Suspense>
);

type AppRoutesProps = {
  onLogout: () => void;
  authMode: 'msal' | 'otp' | 'keycloak' | 'none';
};

function TRMCreateInquiryWrapper(props: any) {
  const location = useLocation();

  return <TRMCreateInquiry setToogleNotification={props?.setToogleNotification} key={location.key} />;
}

const AppRoutes = ({ onLogout, authMode }: AppRoutesProps) => {
  const deviceContext=use(DeviceDetailsContext)
  const {isUserDetailsLoading,userRoles}=use(UserContext)
  const {isMetadataLoading}=use(MetadataContext)
  const [windowSize, setWindowSize] = useState(getWindowSize())
  const [isMobileApp, setIsMobileApp] = useState(false)
  const [toogleNotification,setToogleNotification] = useState<boolean>(false)
  const [isLocationPath,setIsLocationPath]=useState('')
  const bottomNavHideArr=['/inquiries','/','/work-orders']
  const MobileMenuRoutes = ['/', '/inquiries', '/work-orders']
  const TopHeader=['/']
  const location=useLocation()
  function getWindowSize() {
    const { innerWidth, innerHeight } = window
    return { innerWidth, innerHeight }
  }
  // Load side-nav pages right after login so a click renders immediately; the most used go first.
  useEffect(() => {
    if (isUserDetailsLoading) return
    Promise.all([TRMInquiries.preload(), TRMWorkOrders.preload()]).then(() => {
      sideNavPages.forEach(page => page.preload())
    })
  }, [isUserDetailsLoading])
  useEffect(() => {
    if (deviceContext.isDeviceLoaded && (windowSize.innerWidth < 1024)) {
      setIsMobileApp(true)
    } else {
      setIsMobileApp(false)
    }
  }, [windowSize,deviceContext])
  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(prev => {
        const newSize = getWindowSize()
        return prev.innerWidth !== newSize.innerWidth ? newSize : prev
      })
    }
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])
  useEffect(()=>{
    if(window.location.pathname !==''){
      setIsLocationPath(window.location.pathname)
    }
  },[window.location.pathname])
  const AppWrapper=!isMobileApp || TopHeader.includes(location.pathname)
  function findDashboard(){
    if(isUserDetailsLoading){
      return <></>
    }else{
      if(userRoles?.isTRMAdmin || userRoles?.isTRMInbound || userRoles?.isTRMOutbound || userRoles?.isTRMSuperAdmin ||userRoles?.isSiteAdmin || userRoles?.isTRMSales){
        return <TRMHome />
      }else if(userRoles?.isTRMProcessor && !isProcessorDisabled){
        return <ProcessorDashboard/>
      }else if(userRoles?.isCustomer || userRoles?.isTRMProcessor){
        return <CustomerDashboard/>
      }
    }
  }

  return (
    <>
    {isUserDetailsLoading && isMetadataLoading ?<PageOverlay/>:
      <div className={`${isMobileApp ? 'App-custom-ui' : 'side-menus'}`}>
          <div className='mainWrapper' id='mainwrapper'>
            {(!isMobileApp || MobileMenuRoutes.includes(location.pathname)) && <Menus />}
            <div className='content-wrapper'>
             {(!isMobileApp || TopHeader.includes(location.pathname)) && <Header toogleNotification={toogleNotification} onLogout={onLogout} />}
              <div className={`wrapper ${AppWrapper ? '': 'app-wrapper'}`} id='wrapper' role="main">
                <RouteErrorBoundary resetKey={location.pathname}>
                <Suspense fallback={<PageOverlay/>}>
                <Routes>
                  <Route path="/" element={withPageLoader('dashboard', findDashboard())} />
                  <Route path="/create-inquiry" element={withPageLoader('create-inquiry', <TRMCreateInquiryWrapper setToogleNotification={setToogleNotification}/>)} />
                  <Route path="/inquiries" element={withPageLoader('inquiries', <TRMInquiries />)} />
                  <Route path="/inquiries-detailview/:id" element={<TRMDetailview setToogleNotification={setToogleNotification}/>} />
                  <Route path="/workorders-detailview/:id" element={<TRMDetailview setToogleNotification={setToogleNotification}/>} />
                  <Route path="/create-inquiry/:id" element={withPageLoader('create-inquiry', <TRMCreateInquiryWrapper setToogleNotification={setToogleNotification}/>)} />
                  <Route path="/work-orders" element={withPageLoader('work-orders', <TRMWorkOrders />)} />
                  <Route path="/archived" element={withPageLoader('archived', <TRMArchived/>)}/>
                  <Route path="/master-activity-log" element={withPageLoader('master-activity-log', <TRMMasterActivityLog />)} />
                  <Route path="/knowledge-graph" element={withPageLoader('knowledge-graph', <TRMKnowledgeGraph />)} />
                  <Route path="/qanda" element={withPageLoader('qanda', <TRMQandA />)} />
                  <Route path="/announcements" element={withPageLoader('announcements', <TRMAnnouncements />)} />
                  <Route path="/calendars" element={withPageLoader('calendars', <TRMCalendar/>)} />
                  <Route path="/settings" element={withPageLoader('settings', <TRMSettings />)} />
                  <Route path="/site-feedback" element={withPageLoader('site-feedback', <TRMSiteFeedback />)} />
                  <Route path="/Release-Notes" element={withPageLoader('Release-Notes', <TRMReleaseNotes />)} />
                  <Route path="/app-config" element={<Generate />} />
                  <Route
                    path="/renewals"
                    element={withPageLoader('renewals',
                      isUserDetailsLoading ? <></> :
                      (!(userRoles.isTRMSuperAdmin || userRoles.isTRMAdmin || userRoles.isTRMInbound || userRoles.isTRMOutbound || userRoles.isTRMSales  )) ? <Navigate to="/accessdenied" replace /> :
                      <TRMRenewal />
                    )}
                  />
                  <Route path="/reports-data" element={withPageLoader('reports-data',
                      isUserDetailsLoading ? <></> :
                      (!(userRoles.isTRMSuperAdmin || userRoles.isTRMAdmin )) ? <Navigate to="/accessdenied" replace /> :
                      <TRMReportsData />
                    )} />
                  <Route path="/chatbot" element={<Chatbot />} />
                  <Route path="/accessdenied" element={<AccessDenied/>}/>
                  <Route
                    path="/otplogin"
                    element={
                      authMode !== 'none' ? (
                        <Navigate to="/" replace />
                      ) : (
                        <OtpLogin isAuthenticated={false} onLoginSuccess={() => {}} />
                      )
                    }
                  />
                  <Route path="/profile" element={<TRMProfile />} />
                  <Route path="/signout" element={<Logout/>}/>
                  <Route path="/import-data" element={withPageLoader('import-data', <TRMImportData setToogleNotification={setToogleNotification} />)} />
                  <Route path="/global-search" element={<TRMGlobalSearch />} />
                </Routes>
                </Suspense>
                </RouteErrorBoundary>
              </div>
            </div>
            {isMobileApp && bottomNavHideArr.includes(location.pathname) && <div className='footer-menu w-100'>
              <ul className="app-menu-list d-flex">
                <li>
                  <NavLink to='/' className={({ isActive }) => (isActive ? 'active' : '')}>
                    <div className='icon-home-app'></div>
                    <p>Home</p>
                  </NavLink>
                </li>
                {!userRoles?.isTRMProcessor && !isProcessorDisabled && <>
                  <li>
                    <NavLink to='/inquiries' className={({ isActive }) => (isActive ? 'active' : '')}>
                      <div className='icon-inquiries-app'></div>
                      <p>Inquiries</p>
                    </NavLink>
                  </li>
                <li className='add-form'>
                  <NavLink to='/create-inquiry'>
                    <div className='icon-rb-add'><span className='font-0'>Create Inquiry</span></div>
                  </NavLink>
                </li>
                 <li>
                  <NavLink to='/work-orders' className={({ isActive }) => (isActive ? 'active' : '')}>
                    <div className='icon-work-orders-menu'></div>
                    <p>Work Orders</p>
                  </NavLink>

                </li></>}
                {userRoles?.isTRMProcessor && !isProcessorDisabled && <>
                  <li>
                    <NavLink to='/knowledge-graph' className={({ isActive }) => (isActive ? 'active' : '')}>
                      <div className='icon-rb-book-open'></div>
                      <p>Knowledge Graph</p>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to='/qanda' className={({ isActive }) => (isActive ? 'active' : '')}>
                      <div className='icon-rb-question-thick'></div>
                      <p>Q & A</p>
                    </NavLink>
                  </li>
                </>}
                <li>
                  <NavLink to='/chatbot' className={({ isActive }) => (isActive ? 'active' : '')}>
                    <div className='icon-chatbot-menu'></div>
                    <p>Chat Bot</p>
                  </NavLink>
                </li>
               
              </ul>
            </div>}
          </div>
      </div>}
    </>
  )
}

export default AppRoutes
